import { memo, Suspense, useEffect, useMemo, useRef } from 'react';
import { Outlet, useSearchParams } from 'react-router';
import { ScrollRestoration } from 'react-router-dom';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';

import Header from '../Header/Header';
import styles from './AppLayout.module.css';
import { LottieProvider, SVGGradients } from '@bonanza-social/moonlight';
import { LevelUpAnimation } from '@bonanza-social/levels';
import { StatusProgressAnimation } from '@bonanza-social/status';
import {
  ModalsManager,
  BottomSheetsManager,
  NavigationRefComponent,
  ThemedNavigationContainer,
  useLocation,
  Modal,
  ErrorBoundaryProvider,
} from '@bonanza-social/navigation';
import NavBar from '../NavBar/NavBar';
import { Footer } from '@bonanza-social/account';
import { useTranslation } from '@bonanza-social/i18n';
import {
  appAuthenticated,
  appOpened,
  useDispatch,
  useSelector,
} from '@bonanza-social/toolkit/state';
import { selectIsLogged } from '@bonanza-social/auth/state';
import { GlobalAlert } from '@bonanza-social/global-alert';
import MetaPixelPageTracker from '../MetaPixelPageTracker';

function AppLayout() {
  const dispatch = useDispatch();
  const authenticatedDispatched = useRef(false);
  const isLogged = useSelector(selectIsLogged);
  const location = useLocation();
  const [t] = useTranslation('common');
  const [searchParams] = useSearchParams();
  const isMobileApp = searchParams.get('isMobileApp') === 'true';
  const isFooterVisible = useMemo(
    () =>
      !isMobileApp &&
      !location.pathname.match('/games/launch') &&
      !location.pathname.match('/interac-redirect'),
    [location.pathname, isMobileApp],
  );

  const isRegisterPage = useMemo(
    () => location.pathname.match('/register'),
    [location.pathname],
  );

  const mainContentClassName = `${styles.mainContent} ${isRegisterPage ? styles.register : ''}`;

  useEffect(() => {
    dispatch(appOpened());
  }, [dispatch]);

  useEffect(() => {
    if (isLogged && !authenticatedDispatched.current) {
      Modal.close();

      dispatch(appAuthenticated());

      authenticatedDispatched.current = true;
    }
  }, [dispatch, isLogged]);

  useEffect(() => {
    if (!isLogged) {
      authenticatedDispatched.current = false;
    }
  }, [isLogged]);

  return (
    <ThemedNavigationContainer>
      <MetaPixelPageTracker />
      <title>{t('seoTitle')}</title>
      <meta property="og:title" content={t('seoTitle')} />
      <meta name="description" content={t('seoDescription')} />
      <meta property="og:description" content={t('seoDescription')} />
      <SVGGradients />
      <ScrollRestoration />
      <LevelUpAnimation />
      <StatusProgressAnimation />
      <div className={styles.fixed} id="fixed">
        <BottomSheetModalProvider>
          <ModalsManager />
          <BottomSheetsManager />
          <LottieProvider />
        </BottomSheetModalProvider>
      </div>
      <div className={styles.layout}>
        <div className={mainContentClassName}>
          {isMobileApp ? null : <Header />}
          {isLogged ? <GlobalAlert /> : null}
          <ErrorBoundaryProvider>
            <Suspense
              fallback={
                <div style={{ height: 'var(--inner-height)' }}>Loading...</div>
              }
            >
              <Outlet />
            </Suspense>
          </ErrorBoundaryProvider>
        </div>
        {isFooterVisible ? <Footer /> : null}
        {isMobileApp ? null : <NavBar />}
      </div>
      <NavigationRefComponent />
    </ThemedNavigationContainer>
  );
}

export default memo(AppLayout);
